/**
 * Fleet race re-processing plan.
 *
 * After race edits (start times, added/removed races, per-race source pills) every boat of the
 * day must re-adopt the fleet-global race numbers and re-stamp Race_number/Leg_number into its
 * parquet. Syncing Postgres `dataset_events` is not enough: the event API never touches disk, and
 * `2_processing.py` is the only writer of the parquet race labels.
 *
 * Phase order (mirrors AdminScriptExecution.correctRaceNumbers):
 *  A0) promote_tagger_races.py x1 per date  — Tagger RACE tags -> per-boat RACE/PRESTART events
 *   A) fleet_race_schedule.py  x1 per date  — canonical race_schedule project object
 *   B) 2_processing.py         xN per boat  — parquet Race/Leg, RACE/PRESTART/LEG events,
 *                                and race/leg tags on existing phase and maneuver events
 *   C) 0_race.py               xN per boat  — first-time races only (mode 'initial'), or includeRaceStats
 *   C2) 0_race_fleet.py        x1 per date  — Events modes: course, DTL, drivers, race_timeline
 *   C3) 0_day_race_summary.py  xN per boat  — first-time races only
 *   D) 0_map.py                xN per boat  — FleetMap
 *   E) import_descriptions.py  x1 per date  — description, Dataset_type, Race_type (both modes)
 *   F) 4_cleanup.py            x1 per date  — race positions and day pages, first-time races only
 *   G) Race_number_migrate.py  x1 per date  — re-encode nested numbers that are already positive.
 *      Phase B is what assigns a race to events still tagged -1. This step does not.
 *
 * The serial worker claims jobs `ORDER BY priority DESC, created_at ASC` and runs one at a time,
 * so sequential enqueue at a UNIFORM priority is a sufficient ordering guarantee. Never assign
 * per-phase priorities — that would break intra-plan ordering.
 */
import { submitScriptJob } from '@utils/scriptJobs';

export const PROMOTE_TAGGER_RACES_SCRIPT = 'promote_tagger_races.py';
export const FLEET_RACE_SCHEDULE_SCRIPT = 'fleet_race_schedule.py';
export const PROCESSING_SCRIPT_FILE = '2_processing.py';
export const RACE_SCRIPT_FILE = '0_race.py';
export const RACE_FLEET_SCRIPT = '0_race_fleet.py';
export const DAY_RACE_SUMMARY_SCRIPT = '0_day_race_summary.py';
export const MAP_SCRIPT_FILE = '0_map.py';
export const DESCRIPTIONS_SCRIPT = 'import_descriptions.py';
export const CLEANUP_SCRIPT = '4_cleanup.py';
export const RACE_NUMBER_MIGRATE_SCRIPT = 'Race_number_migrate.py';

export type RaceReprocessPhase =
  | 'promote'
  | 'schedule'
  | 'processing'
  | 'race'
  | 'fleet'
  | 'summary'
  | 'map'
  | 'descriptions'
  | 'cleanup'
  | 'migrate';

/**
 * initial — the day had no races, or races without stats. Run the upload race pipeline:
 * labels, race/prestart stats, fleet scoring, day summary, descriptions/tags, race positions, nested tags.
 * update — races and stats already exist. Restamp windows and source membership, rescore the fleet, then refresh tags.
 */
export type RaceReprocessMode = 'initial' | 'update';

export interface PriorRaceState {
  hadRaces: boolean;
  hadStats: boolean;
}

/** A stored race_stats row (events/info LEFT JOIN). Null duration means the race was never computed. */
export function raceRowHasStats(row: { duration_sec?: unknown } | null | undefined): boolean {
  const value = row?.duration_sec;
  if (value == null || value === '') {
    return false;
  }
  const n = Number(value);
  return Number.isFinite(n);
}

/** Fold one boat's stored RACE rows into whether this day already had races and stats. */
export function priorRaceStateFromRows(
  rows: Array<{ Race_number?: unknown; duration_sec?: unknown }> | null | undefined
): PriorRaceState {
  let hadRaces = false;
  let hadStats = false;
  for (const row of rows ?? []) {
    const n = Number(row?.Race_number);
    if (!Number.isFinite(n) || n <= 0) {
      continue;
    }
    hadRaces = true;
    if (raceRowHasStats(row)) {
      hadStats = true;
    }
  }
  return { hadRaces, hadStats };
}

export function mergePriorRaceState(a: PriorRaceState, b: PriorRaceState): PriorRaceState {
  return {
    hadRaces: a.hadRaces || b.hadRaces,
    hadStats: a.hadStats || b.hadStats,
  };
}

/** Update path only when both races and stats were already stored. */
export function raceReprocessMode(prior: PriorRaceState): RaceReprocessMode {
  if (prior.hadRaces && prior.hadStats) {
    return 'update';
  }
  return 'initial';
}

export interface RaceReprocessDataset {
  dataset_id: number;
  source_name: string;
  /** Resolved ISO window. Caller falls back to timezone day bounds when there is no DATASET event. */
  start_time: string;
  end_time: string;
}

export interface RaceReprocessOptions {
  projectId: string;
  className: string;
  /** Any of 'YYYYMMDD' | 'YYYY-MM-DD' | 'YYYY/MM/DD'; normalized to YYYYMMDD. */
  date: string;
  timezone: string;
  datasets: RaceReprocessDataset[];
  /** dataset_ids already reprocessed inline (wizard primary) — skipped in phase B only. */
  skipProcessingDatasetIds?: number[];
  /** True when phase A was already awaited inline by the caller. */
  skipSchedule?: boolean;
  /** True when phase A0 was already awaited inline, or when Tagger promotion is not wanted. */
  skipPromote?: boolean;
  /** One-line switch for phase C. Default false; the admin page passes true. Ignored when `mode` is set. */
  includeRaceStats?: boolean;
  /**
   * Events save path. `initial` runs the upload race follow-up (stats, day summary, positions).
   * `update` restamps race info and refreshes tags. Omit to keep the admin plan.
   */
  mode?: RaceReprocessMode;
  /** Kept on the dataset row by import_descriptions. Blank keeps whatever is already stored. */
  eventName?: string;
}

export interface RaceReprocessJob {
  phase: RaceReprocessPhase;
  script_name: string;
  parameters: Record<string, unknown>;
  dataset_id?: number;
  source_name?: string;
  /** Progress/label text, e.g. "Reprocess events — SUI". */
  label: string;
}

export interface RaceReprocessResult {
  queued: number;
  failed: Array<{ job: RaceReprocessJob; error: string }>;
  /** True when the run was cut short because the schedule job could not be queued. */
  aborted: boolean;
}

/** Normalize any supported date form to YYYYMMDD. */
export function normalizeJobDate(date: string): string {
  return String(date ?? '').replace(/[-/]/g, '');
}

/** Race edits exist and this edit set has not been queued yet. */
export function shouldFleetReprocess(state: {
  raceTimesChanged: boolean;
  raceSourcesTouched: boolean;
  alreadyQueued: boolean;
}): boolean {
  if (state.alreadyQueued) {
    return false;
  }
  return state.raceTimesChanged || state.raceSourcesTouched;
}

/**
 * Time-window, headsail, or crew edits need `2_processing.py` so parquet picks up the tags.
 * Skip when the race fan-out already includes that script, and when the day was cleared of races:
 * processing would rebuild races from telemetry.
 */
export function shouldStructuralReprocess(state: {
  structuralEventsChanged: boolean;
  fleetReprocess: boolean;
  racesCleared: boolean;
}): boolean {
  if (!state.structuralEventsChanged || state.fleetReprocess || state.racesCleared) {
    return false;
  }
  return true;
}

/** Standard per-boat parameter shape shared by 0_race.py and 0_map.py. */
function perBoatParams(
  opts: RaceReprocessOptions,
  dateNorm: string,
  ds: RaceReprocessDataset
): Record<string, unknown> {
  return {
    project_id: opts.projectId,
    class_name: opts.className,
    dataset_id: String(ds.dataset_id),
    date: dateNorm,
    source_name: ds.source_name,
    start_time: null,
    end_time: null,
    timezone: opts.timezone,
    batch: true,
    verbose: false,
  };
}

/**
 * Pure. Returns the jobs in the exact order they must be enqueued.
 *
 * Phase B deliberately passes ONLY the Dataset event, never the primary boat's Headsail/Crew
 * segments: `preserve_events: true` makes each boat re-fetch its own CREW/HEADSAIL, while the
 * Dataset entry is what seeds Grade=3 on non-race rows (2_processing.py processData event loop).
 */
export function buildRaceReprocessJobs(opts: RaceReprocessOptions): RaceReprocessJob[] {
  if (!opts.datasets || opts.datasets.length === 0) {
    // Never queue a bare day-level job with no boats to act on.
    return [];
  }

  const dateNorm = normalizeJobDate(opts.date);
  const skip = new Set(opts.skipProcessingDatasetIds ?? []);
  const jobs: RaceReprocessJob[] = [];
  const initial = opts.mode === 'initial';
  const update = opts.mode === 'update';
  const includeRaceStats = opts.mode ? initial : Boolean(opts.includeRaceStats);

  // Phase A0 — Tagger RACE tags become per-boat RACE/PRESTART events, which phase A then treats as
  // the authoritative (reviewed) race set. Must precede A. No-ops when the day has no promotable
  // tags, or when the Events wizard owns the day (see promote_tagger_races.py).
  if (!opts.skipPromote) {
    jobs.push({
      phase: 'promote',
      script_name: PROMOTE_TAGGER_RACES_SCRIPT,
      label: 'Promote tagged races',
      parameters: {
        class_name: opts.className,
        project_id: opts.projectId,
        date: dateNorm,
        timezone: opts.timezone,
        verbose: false,
      },
    });
  }

  // Phase A — fleet-global race numbers, built from the reviewed RACE events. Must precede B.
  if (!opts.skipSchedule) {
    jobs.push({
      phase: 'schedule',
      script_name: FLEET_RACE_SCHEDULE_SCRIPT,
      label: 'Fleet race schedule',
      parameters: {
        class_name: opts.className,
        project_id: opts.projectId,
        date: dateNorm,
        timezone: opts.timezone,
        // verbose: the schedule listing (one line per race, with reviewed/locked flags) is the only
        // record of what phase B was told to stamp. Without it a race-numbering incident cannot be
        // diagnosed after the fact, and script_jobs.output_summary keeps only the last 20 lines.
        verbose: true,
      },
    });
  }

  // Phase B — the only writer of parquet Race_number/Leg_number.
  for (const ds of opts.datasets) {
    if (skip.has(ds.dataset_id)) {
      continue;
    }
    jobs.push({
      phase: 'processing',
      script_name: PROCESSING_SCRIPT_FILE,
      dataset_id: ds.dataset_id,
      source_name: ds.source_name,
      label: `Reprocess events — ${ds.source_name}`,
      parameters: {
        project_id: opts.projectId,
        class_name: opts.className,
        dataset_id: String(ds.dataset_id),
        date: dateNorm,
        source_name: ds.source_name,
        start_time: ds.start_time,
        end_time: ds.end_time,
        events: [
          {
            EventType: 'Dataset',
            Event: 'Active',
            Start: ds.start_time,
            End: ds.end_time,
          },
        ],
        preserve_events: true,
        day_type: ['TRAINING', 'RACING'],
        race_type: ['INSHORE', 'COASTAL', 'OFFSHORE'],
        verbose: false,
      },
    });
  }

  // Phase C — race/prestart stats. First-time races, or the admin includeRaceStats switch.
  if (includeRaceStats) {
    for (const ds of opts.datasets) {
      jobs.push({
        phase: 'race',
        script_name: RACE_SCRIPT_FILE,
        dataset_id: ds.dataset_id,
        source_name: ds.source_name,
        label: `Race + prestart stats — ${ds.source_name}`,
        parameters: perBoatParams(opts, dateNorm, ds),
      });
    }
  }

  // One fleet pass for the day, same job as Admin → Race stats: fleet. It writes race_timeline,
  // the course, DTL and the finish gaps the Race Summary page reads. It has to follow the parquet
  // labels, and on a first-time day it has to follow 0_race.py. Day summary and position cleanup
  // then see that timeline, so cleanup does not overwrite the fleet positions.
  if (initial || update) {
    jobs.push({
      phase: 'fleet',
      script_name: RACE_FLEET_SCRIPT,
      label: 'Race stats: fleet',
      parameters: {
        class_name: opts.className,
        project_id: opts.projectId,
        date: dateNorm,
        verbose: false,
      },
    });
  }

  // Day summary reads the race_stats the fleet pass just refreshed. Upload runs this from 3_execute after Race.
  if (initial) {
    for (const ds of opts.datasets) {
      jobs.push({
        phase: 'summary',
        script_name: DAY_RACE_SUMMARY_SCRIPT,
        dataset_id: ds.dataset_id,
        source_name: ds.source_name,
        label: `Day race summary — ${ds.source_name}`,
        parameters: perBoatParams(opts, dateNorm, ds),
      });
    }
  }

  // Phase D — every boat, including any skipped in phase B: their maps still need regenerating.
  for (const ds of opts.datasets) {
    jobs.push({
      phase: 'map',
      script_name: MAP_SCRIPT_FILE,
      dataset_id: ds.dataset_id,
      source_name: ds.source_name,
      label: `Regenerate map — ${ds.source_name}`,
      parameters: perBoatParams(opts, dateNorm, ds),
    });
  }

  // Description, Dataset_type / Race_type, and the DATASET event tags. Both modes: a first-time
  // day has to leave TRAINING, and an edit of starts or source membership changes which races
  // the description lists. Blank event_name is preserved by the script.
  if (initial || update) {
    jobs.push({
      phase: 'descriptions',
      script_name: DESCRIPTIONS_SCRIPT,
      label: 'Update descriptions and tags',
      parameters: {
        class_name: opts.className,
        project_id: opts.projectId,
        event_name: opts.eventName ?? '',
        timezone: opts.timezone,
        dates: [dateNorm],
        settle_seconds: 0,
      },
    });
  }

  // Race positions and the RACE SUMMARY page. Needs the stats phase C just wrote, so first-time only.
  if (initial) {
    jobs.push({
      phase: 'cleanup',
      script_name: CLEANUP_SCRIPT,
      label: 'Race positions and day pages',
      parameters: {
        class_name: opts.className,
        project_id: opts.projectId,
        date: dateNorm,
        verbose: false,
        include_gas: false,
        race_followup: true,
      },
    });
  }

  // Phase G — encode nested RACES.Race_number tags only. Parquet and top-level tags are already
  // correct from phase B, so scope=nested and include_parquet=false. The script derives each label
  // from the event's own race number and day; it reads no schedule.
  jobs.push({
    phase: 'migrate',
    script_name: RACE_NUMBER_MIGRATE_SCRIPT,
    label: 'Remap nested race tags',
    parameters: {
      class_name: opts.className,
      project_id: opts.projectId,
      date: dateNorm,
      timezone: opts.timezone,
      dry_run: false,
      scope: 'nested',
      include_parquet: false,
      verbose: false,
    },
  });

  return jobs;
}

/** Injectable for tests; defaults to the real durable-queue submit. */
export type SubmitJobFn = (job: RaceReprocessJob, projectId: string, className: string) => Promise<void>;

const defaultSubmit: SubmitJobFn = async (job, projectId, className) => {
  await submitScriptJob(
    {
      class_name: className,
      script_name: job.script_name,
      parameters: job.parameters,
      project_id: projectId,
    },
    // Keep ~26 queued jobs out of processStore so they don't flood it with toasts or collide
    // with the wizard's own SSE waits.
    { trackProcess: false }
  );
};

/**
 * Enqueue sequentially — order is load-bearing.
 *
 * A failed `schedule` enqueue aborts the whole run: without a fresh schedule every boat would
 * re-stamp against a stale one, which is silently wrong and worse than doing nothing. Per-boat
 * failures are collected and the run continues, since the plan is idempotent and re-runnable.
 *
 * A failed `promote` enqueue does NOT abort: phase A still rebuilds the schedule from the existing
 * dataset_events, which is exactly the pre-promotion behaviour. Aborting would leave the day both
 * untouched and stale, which is strictly worse.
 */
export async function enqueueRaceReprocessJobs(
  jobs: RaceReprocessJob[],
  opts: { projectId: string; className: string },
  hooks?: {
    onProgress?: (queued: number, total: number, job: RaceReprocessJob) => void;
    submit?: SubmitJobFn;
  }
): Promise<RaceReprocessResult> {
  const submit = hooks?.submit ?? defaultSubmit;
  const result: RaceReprocessResult = { queued: 0, failed: [], aborted: false };

  for (let i = 0; i < jobs.length; i++) {
    const job = jobs[i];
    try {
      await submit(job, opts.projectId, opts.className);
      result.queued += 1;
      hooks?.onProgress?.(result.queued, jobs.length, job);
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err);
      result.failed.push({ job, error });
      if (job.phase === 'schedule') {
        result.aborted = true;
        return result;
      }
    }
  }

  return result;
}

/** Human-readable summary for the completion toast. */
export function summarizeRaceReprocess(
  result: RaceReprocessResult,
  totalJobs: number,
  boatCount: number,
  mode?: RaceReprocessMode
): string {
  if (result.aborted) {
    return 'Could not queue the fleet race schedule, so no jobs were queued. Try again, or use Admin → Correct race numbers.';
  }
  if (result.failed.length > 0) {
    const names = result.failed
      .map((f) => f.job.source_name)
      .filter((n): n is string => Boolean(n));
    const who = names.length > 0 ? ` (${Array.from(new Set(names)).join(', ')})` : '';
    // Phase A0 has no source_name, so name it explicitly or its failure reads as a nameless one.
    const promoteFailed = result.failed.some((f) => f.job.phase === 'promote')
      ? ' Tagged races were not promoted, so races come from the existing events.'
      : '';
    return `Queued ${result.queued} of ${totalJobs} jobs; ${result.failed.length} failed to queue${who}.${promoteFailed} Re-run from Admin → Correct race numbers.`;
  }
  const trail = mode === 'initial'
    ? 'tagged races → schedule → reprocess → race stats → fleet scoring → day summary → maps → descriptions → race positions → tag remap'
    : mode === 'update'
      ? 'tagged races → schedule → reprocess → fleet scoring → maps → descriptions and tags → tag remap'
      : 'tagged races → schedule → reprocess → maps → tag remap';
  return `${result.queued} job(s) for ${boatCount} boat(s): ${trail}. Track progress in Admin → Script Execution.`;
}
